import React, { useState } from 'react'
import { PaperClipOutlined, LoadingOutlined, CloudUploadOutlined } from '@ant-design/icons'
import { Button, Col, Form, Input, Row, Space, Modal, Typography, Card, Upload, Spin } from 'antd'
import { DeleteIcon, EmptyHeaderHero, GlobalHeader, GlobalFooter } from './components'
import { sendEmailForPIPDocumentUpload } from '../services/Api'
import ReCAPTCHA from 'react-google-recaptcha'

import NJPIPStyle from '../../styles/NJPIPUpload.module.css'
import SubmitDocStyle from '../../styles/SubmitDocuments.module.css'
import TokenParser from './TokenParser'

const { Text } = Typography

const { Dragger } = Upload

const { TextArea } = Input

const FILE_UPLOAD_SIZE = 5
const antIcon = <LoadingOutlined spin style={{ fontSize: 24 }} />

const USER_JSON_MAP = {
  firstName: 'First Name',
  lastName: 'Last Name',
  policyNumber: 'Policy Number',
  emailAddress: 'Email Address',
  state: 'State',
  description: 'description'
}


const NewJerseyPIPDocument = () => {
  const [fileList, setFileList] = useState([])
  const [visible, setVisible] = useState(false)
  const [fileIndex, setFileIndex] = useState(-1)
  const [user, setUser] = useState({
    firstName: '',
    lastName: '',
    policyNumber: '',
    emailAddress: '',
    state: 'NJ'
  })
  const [showSpinner, setShowSpinner] = useState(false)
  const [message, setMessage] = useState(null)
  const [uploadMessage, setUploadMessage] = useState(null)
  const [recaptcha, setRecaptcha] = useState('')
  const [isPrefilled, setIsPrefilled] = useState(false)

  const props = {
    name: 'file',
    accept: '.jpeg,.png,.pdf,.heic',
    beforeUpload: file => {
      setUploadMessage('')
      let newFileList = [...fileList]
      const isLt2M = file.size / 1024 / 1024 < FILE_UPLOAD_SIZE
      if (!isLt2M) {
        setUploadMessage(`Document must be smaller than ${FILE_UPLOAD_SIZE}MB!`)
        return
      }
      newFileList.push(file)
      setFileList(newFileList)
    },
    fileList,
    maxCount: 1,
    onRemove: () => {
      setFileList([])
    }
  }

  const uploadProps = {
    name: 'file',
    accept: '.jpeg,.png,.pdf,.heic',
    onChange: (props) => {
      setUploadMessage('')
      const { file } = props
      const { originFileObj } = file
      if (file.status !== 'uploading') {
        let newFileList = [...fileList]
        const isLt2M = file.size / 1024 / 1024 < FILE_UPLOAD_SIZE
        if (!isLt2M) {
          setUploadMessage(`Document must be smaller than ${FILE_UPLOAD_SIZE}MB!`)
          return
        }
        newFileList.push(originFileObj)
        setFileList(newFileList)
      }
    }
  }

  const updateTextFields = (text, field) => {
    const clonedUser = { ...user }
    clonedUser[field] = text
    setUser(clonedUser)
  }

  const removeFile = fileIndex => {
    setUploadMessage('')
    setFileIndex(fileIndex)
    setVisible(true)
  }

  const handleDelete = () => {
    setUploadMessage('')
    const newFileList = [...fileList]
    newFileList.splice(fileIndex, 1)
    setFileList(newFileList)
    setVisible(false)
    setFileIndex(-1)
  }

  const handleSubmit = async () => {
    setUploadMessage('')
    setMessage(null)
    setShowSpinner(true)

    let totalFileSize = 0
  
    for (let i = 0; i < fileList.length; i++) {
      totalFileSize += fileList[i].size
    }

    const isLt2M = totalFileSize / 1024 / 1024 < FILE_UPLOAD_SIZE

    if (!isLt2M) {
      setUploadMessage(`Document must be smaller than ${FILE_UPLOAD_SIZE}MB!`)
      setShowSpinner(false)
      return
    }

    const finalJson = stripFinalJson(user)
    const htmlText = buildTable(finalJson)

    await sendEmailForPIPDocumentUpload(fileList, htmlText, user)
    setMessage('')
    setShowSpinner(false)

    const newWindow = window.open('/g/submit-documents/pip-document-upload-results', '_self', 'noopener,noreferrer')
    if (newWindow) newWindow.opener = null
  }

  const isFormValid = () => {
    let response = false
    if (
      user.firstName.length === 0 ||
      user.lastName.length === 0 ||
      user.policyNumber.length === 0 ||
      user.emailAddress.length === 0 ||
      recaptcha.length === 0 ||
      fileList.length === 0
    ) {
      response = true
    }
    if (!response) {
      const regEx = /\S+@\S+\.\S+/
      if (user.emailAddress.length > 0 && !regEx.test(user.emailAddress)) {
        response = true
      }
    }
    return response
  }

  const stripFinalJson = user => {
    const {
      policyNumber,
      emailAddress,
      firstName,
      lastName,
      state
    } = user
    const obj = {
      firstName,
      lastName,
      policyNumber,
      emailAddress,
      state
    }
    return obj
  }

  const buildTable = userInfo => {
    var table = '<table border="1">'
    table += '<tr><td colspan="2"><b>New Jersey No-Fault PIP Document Upload</b></td></tr><tr align="center"><td colspan="2"><b>Policyholder Information</b></td></tr>'
    for (const key in userInfo) {
      table += `<tr><td>${USER_JSON_MAP[key]}</td><td>${userInfo[key]}</td></tr>`
    }
    table += '</table>'
    return table
  }

  const handleKeyDown = event => {
    if (event.key === 'Enter' && !isFormValid()) {
      handleSubmit()
    }
  }

  const handleCancel = () => {
    setVisible(false)
    setFileIndex(-1)
  }

  const onExpiredRecaptcha = () => {
    window.grecaptcha.reset()
  }

  return (
    <div className="container">
      <GlobalHeader />
      <>
        <EmptyHeaderHero className={`${SubmitDocStyle["EmptyHeaderHeroUploadDocs"]}`} leftAlign={true} minHeight={true} title="New Jersey - Submit Requested Files" />
        <div className={`${NJPIPStyle["nj-pip-upload"]} express-policy-changes`}>
          <Row>
            <Card className={`${NJPIPStyle["register-card"]}`}>
              <h5 className={`${NJPIPStyle["sub-label"]}`}>Policy Information</h5>
              <Row>
                <Col xl={{ span: 8 }} xs={{ span: 24 }}>
                  <Form.Item className={`${NJPIPStyle["margin-bottom-14"]}`}>
                    <Input
                      autoComplete="off"
                      className={`${NJPIPStyle["input-text"]}`}
                      placeholder="First Name*"
                      onChange={event => updateTextFields(event.target.value, 'firstName')}
                      onKeyDown={handleKeyDown}
                      value={user.firstName}
                      disabled={isPrefilled}
                    />
                  </Form.Item>
                </Col>
                <Col xl={{ span: 8, offset: 1 }} xs={{ span: 24 }}>
                  <Form.Item className={`${NJPIPStyle["margin-bottom-14"]}`}>
                    <Input
                      autoComplete="off"
                      className={`${NJPIPStyle["input-text"]}`}
                      placeholder="Last Name*"
                      onChange={event => updateTextFields(event.target.value, 'lastName')}
                      onKeyDown={handleKeyDown}
                      value={user.lastName}
                      disabled={isPrefilled}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col xl={{ span: 8 }} xs={{ span: 24 }}>
                  <Form.Item className={`${NJPIPStyle["margin-bottom-14"]}`}>
                    <Input
                      autoComplete="off"
                      className={`${NJPIPStyle["input-text"]}`}
                      placeholder="Email Address*"
                      onChange={event => updateTextFields(event.target.value, 'emailAddress')}
                      onKeyDown={handleKeyDown}
                      value={user.emailAddress}
                      disabled={isPrefilled}
                    />
                  </Form.Item>
                </Col>
                <Col xl={{ span: 8, offset: 1 }} xs={{ span: 24 }}>
                  <Form.Item className={`${NJPIPStyle["margin-bottom-14"]}`}>
                    <Input
                      autoComplete="off"
                      className={`${NJPIPStyle["input-text"]}`}
                      placeholder="Quote/Policy Number*"
                      onChange={event => updateTextFields(event.target.value, 'policyNumber')}
                      onKeyDown={handleKeyDown}
                      value={user.policyNumber}
                      disabled={isPrefilled}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <h5 className={`${NJPIPStyle["sub-label"]} ${NJPIPStyle["margin-top-8"]}`}>Upload Documents</h5>
              <Row className={`${NJPIPStyle["margin-top-16"]}`}>
                <Col xl={{ span: 8 }} xs={{ span: 24 }}>
                  {fileList && fileList.length === 0 && (
                    <Dragger {...props}>
                      <p className="ant-upload-drag-icon">
                        <CloudUploadOutlined />
                      </p>
                      <p className="ant-upload-text">
                        Drag and drop your file here
                        <br />
                        or <a>browse</a> your computer
                      </p>
                    </Dragger>
                  )}
                </Col>
              </Row>
              <Row>
                <Col xl={{ span: 24 }} xs={{ span: 24 }}>
                  {fileList.map((file, index) => (
                    <>
                      <div key={index} className={`${NJPIPStyle["employee-info-dragger-file-display-div"]}`} style={{ marginTop: 12 }}>
                      <Text className={`${NJPIPStyle["file-label"]}`}>Document {index+1}</Text>
                      <div>
                        <PaperClipOutlined style={{ display: 'initial', marginRight: 6 }} />
                          <p>{file.name} </p>
                          <Button
                            className={`${NJPIPStyle["delete-item"]}`}
                            icon={<DeleteIcon />}
                            onClick={() => removeFile(index)}
                            size="large"
                          />
                      </div>
                      </div>
                    </>
                  ))}
                </Col>
              </Row>
              {fileList && fileList.length > 0 && (
                <Row className={`${NJPIPStyle["margin-top-16"]}`}>
                  <Col xl={{ span: 8 }} xs={{ span: 24 }}>
                    <Upload {...uploadProps}>
                      <Button className={`${NJPIPStyle["new-doc"]}`}>Add Another Document</Button>
                    </Upload>
                  </Col>
                </Row>
              )}
              {uploadMessage && (
                <Row className={`${NJPIPStyle["margin-bottom-14"]}`}>
                  <Col className={`${NJPIPStyle["account-margin-select"]}`} xl={{ span: 8 }} xs={{ span: 24 }}>
                    <Text className={`${NJPIPStyle["error-msg"]}`}>{uploadMessage}</Text>
                  </Col>
                </Row>
              )}
              {message && (
                <Row className={`${NJPIPStyle["margin-bottom-14"]}`}>
                  <Col className={`${NJPIPStyle["account-margin-select"]}`} xl={{ span: 8 }} xs={{ span: 24 }}>
                    {/* {message !== 'Account Created' && <Text className={`${NJPIPStyle["error-msg"]}`}>{message}</Text>} */}
                  </Col>
                </Row>
              )}
              {/* {isFormValid() && (
                <Row className="margin-14">
                  <Col className={`${NJPIPStyle["account-margin-select"]}`} xl={{ span: 8 }} xs={{ span: 24 }}>
                    <Text className={`${NJPIPStyle["error-msg"]}`}>Please complete all required fields.</Text>
                  </Col>
                </Row>
              )} */}
              <div style={{height: '14px'}}/>
              <h5 className={`${NJPIPStyle['sub-label']} ${NJPIPStyle['margin-top-8']}`}>Description</h5>
              <Row>
                <Col xl={{ span: 8 }} xs={{ span: 24 }}>
                  <Form.Item className={`${NJPIPStyle['margin-bottom-14']}`}>
                    <TextArea
                      className={`${NJPIPStyle['text-area']}`}
                      onChange={event => updateTextFields(event.target.value, 'description')}
                      onKeyDown={handleKeyDown}
                      value={user.description}
                      autoSize={{ minRows: 1, maxRows: 10 }}
                      maxLength={250}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row className={`${NJPIPStyle["width-808"]}`}>
              <Row className={`${NJPIPStyle["margin-top-16"]}`}>
                  <ReCAPTCHA
                    sitekey="6LcrfAklAAAAAP4dtQHGOvCiwTyMlqtiVNP09968"
                    onChange={(e) => { e !== null ? setRecaptcha(e) : onExpiredRecaptcha() }} />
                </Row>
                <Form.Item className={`${NJPIPStyle["btn-control"]} ${NJPIPStyle["button-submit-form"]}`}>
                  <Space>
                    <Button
                      className={`${NJPIPStyle["register-form-button"]} register`}
                      disabled={isFormValid()}
                      onClick={() => handleSubmit()}
                    >
                      Submit
                    </Button>
                    {showSpinner && <Spin indicator={antIcon} style={{ marginLeft: 24 }} />}
                  </Space>
                </Form.Item>
              </Row>
            </Card>
          </Row>
        </div>
        <Modal
          title="Delete Confirmation"
          visible={visible}
          onOk={handleDelete}
          onCancel={handleCancel}
          okText="Delete"
          cancelText="Cancel"
        >
          <p>Are you sure you want to delete this file?</p>
        </Modal>
        <TokenParser 
          setUser={setUser} 
          setIsPrefilled={setIsPrefilled} 
          fields={["firstName", "lastName", "emailAddress", "policyNumber"]}
        />
      </>
      <GlobalFooter />
    </div>
  )
}

NewJerseyPIPDocument.propTypes = {}

export default NewJerseyPIPDocument
